import { useState, useEffect } from "react";
import { toast } from "sonner";
import { BarChart3, Crown, CheckCircle2, TrendingUp, Activity, AlertCircle, Layers, Timer, Target } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Button } from "@/components/ui/button";
import { formatTime } from "@/utils/constants";

export const DashboardView = ({ analytics, isPro, onShowUpgrade }) => {
  if (!isPro) {
    return (
      <div className="p-8 text-center">
        <div className="max-w-md mx-auto">
          <BarChart3 className="h-16 w-16 text-primary mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-bold mb-2">Unlock Dashboard Analytics</h3>
          <p className="text-muted-foreground mb-4">Get insights into your productivity with detailed charts and metrics.</p>
          <Button onClick={onShowUpgrade} className="bg-gradient-to-r from-primary to-purple-600"><Crown className="h-4 w-4 mr-2" />Upgrade to Pro</Button>
        </div>
      </div>
    );
  }

  const COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B'];

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="dashboard-stat-card p-4 rounded-xl" data-testid="stat-total-tasks">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg"><Target className="h-5 w-5 text-primary" /></div>
            <div><div className="text-2xl font-bold">{analytics?.total_tasks || 0}</div><div className="text-xs text-muted-foreground">Total Tasks</div></div>
          </div>
        </div>
        <div className="dashboard-stat-card p-4 rounded-xl" data-testid="stat-completed">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 rounded-lg"><CheckCircle2 className="h-5 w-5 text-emerald-500" /></div>
            <div><div className="text-2xl font-bold">{analytics?.completed_tasks || 0}</div><div className="text-xs text-muted-foreground">Completed</div></div>
          </div>
        </div>
        <div className="dashboard-stat-card p-4 rounded-xl" data-testid="stat-completion-rate">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg"><TrendingUp className="h-5 w-5 text-blue-500" /></div>
            <div><div className="text-2xl font-bold">{analytics?.completion_rate || 0}%</div><div className="text-xs text-muted-foreground">Completion Rate</div></div>
          </div>
        </div>
        <div className="dashboard-stat-card p-4 rounded-xl" data-testid="stat-week-completed">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/10 rounded-lg"><Activity className="h-5 w-5 text-amber-500" /></div>
            <div><div className="text-2xl font-bold">{analytics?.week_completed || 0}</div><div className="text-xs text-muted-foreground">This Week</div></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl border p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" />Daily Completions</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.daily_completions || []}>
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-xl border p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><Layers className="h-4 w-4 text-primary" />Tasks by Column</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={analytics?.by_column || []} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, count }) => `${name}: ${count}`} labelLine={false}>
                  {(analytics?.by_column || []).map((entry, index) => <Cell key={entry.name} fill={entry.color || COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-xl border p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><AlertCircle className="h-4 w-4 text-primary" />Priority Distribution</h3>
          <div className="space-y-3">
            {[
              { label: 'High', count: analytics?.by_priority?.high || 0, color: 'bg-red-500' },
              { label: 'Medium', count: analytics?.by_priority?.medium || 0, color: 'bg-amber-500' },
              { label: 'Low', count: analytics?.by_priority?.low || 0, color: 'bg-emerald-500' }
            ].map(item => {
              const total = (analytics?.by_priority?.high || 0) + (analytics?.by_priority?.medium || 0) + (analytics?.by_priority?.low || 0);
              const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
              return (
                <div key={item.label} className="space-y-1">
                  <div className="flex justify-between text-sm"><span>{item.label}</span><span className="text-muted-foreground">{item.count} ({pct}%)</span></div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden"><div className={`h-full ${item.color} transition-all`} style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card rounded-xl border p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><Timer className="h-4 w-4 text-primary" />Time Tracked</h3>
          <div className="text-center py-8">
            <div className="text-4xl font-bold text-primary">{formatTime(analytics?.total_time_minutes || 0)}</div>
            <div className="text-sm text-muted-foreground mt-2">Total time tracked across all tasks</div>
          </div>
        </div>
      </div>
    </div>
  );
};
